import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';

const FLOOR_OPTIONS = ['Basement', 'Lower Ground', 'Ground Floor', 'First Floor', 'Second Floor', 'Third Floor', 'Fourth Floor', 'Roof'];
const AREA_TYPE_OPTIONS = [
  { value: 'office', label: 'Office' },
  { value: 'retail', label: 'Retail' },
  { value: 'storage', label: 'Storage' },
  { value: 'ancillary', label: 'Ancillary' },
  { value: 'plant', label: 'Plant' },
  { value: 'circulation', label: 'Circulation' },
];
const LOCATION_OPTIONS = ['internal', 'balcony', 'terrace', 'yard', 'garage', 'store'];

const emptyRow = {
  floor_level: 'Ground Floor', area_type: 'office',
  gross_internal_area: '', net_internal_area: '', usable_area: '',
  ancillary_area: '', circulation_area: '', storage_area: '', plant_area: '',
  notes: '', location_type: 'internal', included_in_valuation: true,
};

export default function ITMSTable({ rows, onAdd, onUpdate, onDelete }) {
  const [newRow, setNewRow] = useState({ ...emptyRow });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});

  const numFields = ['gross_internal_area', 'net_internal_area', 'usable_area', 'ancillary_area', 'circulation_area', 'storage_area', 'plant_area'];

  const parseNums = (data) => {
    const out = { ...data };
    numFields.forEach(f => { out[f] = parseFloat(data[f]) || 0; });
    return out;
  };

  const handleAdd = () => {
    if (!newRow.floor_level) return;
    onAdd(parseNums(newRow));
    setNewRow({ ...emptyRow });
  };

  const startEdit = (row) => { setEditId(row.id); setEditData({ ...row }); };
  const saveEdit = () => { onUpdate(editId, parseNums(editData)); setEditId(null); };

  const totalGIA = rows.reduce((s, r) => s + (r.gross_internal_area || 0), 0);
  const totalNIA = rows.reduce((s, r) => s + (r.net_internal_area || 0), 0);

  return (
    <div className="space-y-3" data-testid="itms-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left p-2 font-medium text-gray-600">Floor</th>
              <th className="text-left p-2 font-medium text-gray-600">Type</th>
              <th className="text-right p-2 font-medium text-gray-600 w-20">GIA (m²)</th>
              <th className="text-right p-2 font-medium text-gray-600 w-20">NIA (m²)</th>
              <th className="text-right p-2 font-medium text-gray-600 w-20">Usable</th>
              <th className="text-right p-2 font-medium text-gray-600 w-20">Ancillary</th>
              <th className="text-right p-2 font-medium text-gray-600 w-20">Circ.</th>
              <th className="text-left p-2 font-medium text-gray-600">Loc.</th>
              <th className="text-center p-2 font-medium text-gray-600 w-14">Val</th>
              <th className="text-left p-2 font-medium text-gray-600">Notes</th>
              <th className="p-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} className="border-b hover:bg-gray-50/50" data-testid={`itms-row-${row.id}`}>
                {editId === row.id ? (
                  <>
                    <td className="p-1">
                      <Select value={editData.floor_level || ''} onValueChange={v => setEditData({...editData, floor_level: v})}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{FLOOR_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-1">
                      <Select value={editData.area_type || 'office'} onValueChange={v => setEditData({...editData, area_type: v})}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{AREA_TYPE_OPTIONS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    {['gross_internal_area','net_internal_area','usable_area','ancillary_area','circulation_area'].map(f => (
                      <td key={f} className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" value={editData[f] || ''} onChange={e => setEditData({...editData, [f]: e.target.value})} /></td>
                    ))}
                    <td className="p-1">
                      <Select value={editData.location_type || 'internal'} onValueChange={v => setEditData({...editData, location_type: v})}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{LOCATION_OPTIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-1 text-center"><input type="checkbox" checked={editData.included_in_valuation !== false} onChange={e => setEditData({...editData, included_in_valuation: e.target.checked})} /></td>
                    <td className="p-1"><Input className="h-7 text-xs" value={editData.notes || ''} onChange={e => setEditData({...editData, notes: e.target.value})} /></td>
                    <td className="p-1 flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={saveEdit}><Check className="h-3 w-3 text-green-600" /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditId(null)}><X className="h-3 w-3" /></Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="p-2 font-medium">{row.floor_level}</td>
                    <td className="p-2 text-gray-500 capitalize">{row.area_type}</td>
                    <td className="p-2 text-right font-mono font-medium">{row.gross_internal_area ? Number(row.gross_internal_area).toFixed(2) : '-'}</td>
                    <td className="p-2 text-right font-mono">{row.net_internal_area ? Number(row.net_internal_area).toFixed(2) : '-'}</td>
                    <td className="p-2 text-right font-mono">{row.usable_area ? Number(row.usable_area).toFixed(2) : '-'}</td>
                    <td className="p-2 text-right font-mono">{row.ancillary_area ? Number(row.ancillary_area).toFixed(2) : '-'}</td>
                    <td className="p-2 text-right font-mono">{row.circulation_area ? Number(row.circulation_area).toFixed(2) : '-'}</td>
                    <td className="p-2 text-gray-500 capitalize">{row.location_type || 'internal'}</td>
                    <td className="p-2 text-center">{row.included_in_valuation !== false ? '✓' : '—'}</td>
                    <td className="p-2 text-gray-400 text-xs truncate max-w-[100px]">{row.notes || '-'}</td>
                    <td className="p-1 flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => startEdit(row)}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onDelete(row.id)}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {/* New row */}
            <tr className="border-b bg-blue-50/30">
              <td className="p-1">
                <Select value={newRow.floor_level} onValueChange={v => setNewRow({...newRow, floor_level: v})}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{FLOOR_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="p-1">
                <Select value={newRow.area_type} onValueChange={v => setNewRow({...newRow, area_type: v})}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{AREA_TYPE_OPTIONS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" placeholder="GIA" value={newRow.gross_internal_area} onChange={e => setNewRow({...newRow, gross_internal_area: e.target.value})} data-testid="new-itms-gia" /></td>
              <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" placeholder="NIA" value={newRow.net_internal_area} onChange={e => setNewRow({...newRow, net_internal_area: e.target.value})} /></td>
              <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" placeholder="Use" value={newRow.usable_area} onChange={e => setNewRow({...newRow, usable_area: e.target.value})} /></td>
              <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" placeholder="Anc" value={newRow.ancillary_area} onChange={e => setNewRow({...newRow, ancillary_area: e.target.value})} /></td>
              <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" placeholder="Circ" value={newRow.circulation_area} onChange={e => setNewRow({...newRow, circulation_area: e.target.value})} /></td>
              <td className="p-1">
                <Select value={newRow.location_type} onValueChange={v => setNewRow({...newRow, location_type: v})}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{LOCATION_OPTIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="p-1 text-center"><input type="checkbox" checked={newRow.included_in_valuation !== false} onChange={e => setNewRow({...newRow, included_in_valuation: e.target.checked})} /></td>
              <td className="p-1"><Input className="h-7 text-xs" placeholder="Notes" value={newRow.notes} onChange={e => setNewRow({...newRow, notes: e.target.value})} /></td>
              <td className="p-1"><Button size="sm" className="h-7 w-7 p-0" onClick={handleAdd} data-testid="add-itms-btn"><Plus className="h-3.5 w-3.5" /></Button></td>
            </tr>
            {/* Totals */}
            {rows.length > 0 && (
              <tr className="bg-gray-100 font-medium">
                <td className="p-2" colSpan={2}>Totals</td>
                <td className="p-2 text-right font-mono">{totalGIA.toFixed(2)}</td>
                <td className="p-2 text-right font-mono">{totalNIA.toFixed(2)}</td>
                <td colSpan={7}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
